import React, { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  FieldMultiSelectFullWidth,
  FieldSelectFullWidth,
} from "@/components/trade-filter-selects";
import { STRATEGY_PINE_SECTION_LABEL_CLASS } from "@/components/Strategies/StrategyPineSectionCard";
import { cn } from "@/lib/utils";
import type {
  StrategyRdCatalog,
  StrategyRdComposerConfluenceSlot,
  StrategyRdComposerEntrySlot,
  StrategyRdComposerSetupSlot,
  StrategyRdConcept,
  StrategyRdConceptSetting,
  StrategyRdEntryMode,
  StrategyRdLogicPath,
  StrategyRdMaxWait,
  StrategyRdPack,
  StrategyRdSlotSide,
} from "../../../types/strategyRd";
import { STRATEGY_RD_CANDLE_TYPES } from "../../../types/strategyRd";
import {
  strategyRdConceptDescriptionForDisplay,
  strategyRdConceptDisplayName,
  strategyRdConceptDescriptionSections,
  strategyRdConceptShortDefinition,
  strategyRdConceptTfGuidance,
  strategyRdEntryModeDefinition,
  strategyRdEntryModeLabel,
  strategyRdLocationBreakerSubtitle,
  strategyRdLocationEntryModeHint,
  strategyRdLocationInteractionHelper,
  strategyRdLocationInteractionMeaning,
} from "../../../utils/strategyRdConceptCopy";
import {
  STRATEGY_RD_SR_SCOPE_OPTIONS,
  normalizeStrategyRdSessionIdsSelection,
  strategyRdAllSessionSentinel,
  strategyRdConfluencesFromCatalog,
  strategyRdConfluencePickerGroups,
  strategyRdHasClearOfOpposingSr,
  strategyRdIsVolumeConfluence,
  strategyRdSessionDisplayName,
  strategyRdSessionPickerGroups,
  strategyRdSessionSelectOptions,
  strategyRdSetupConceptFromCatalog,
  strategyRdSetupNeedsSession,
  strategyRdSetupsFromCatalog,
  strategyRdSetupPickerGroups,
  strategyRdVolumeUnavailable,
  parseStrategyRdEntryMode,
} from "../../../utils/strategyRdConcepts";
import {
  normalizeStrategyRdLevelPolarity,
  normalizeStrategyRdPolarity,
  normalizeStrategyRdSlotSide,
  strategyRdBranchHintLabel,
  strategyRdComposerSummaryColumns,
  strategyRdConceptSideReading,
  strategyRdConfluenceSideOptions,
  strategyRdDefaultConfluenceSide,
  strategyRdDefaultSetupSide,
  strategyRdDefaultSideByLevel,
  strategyRdDefaultSideByLocation,
  strategyRdDefaultSideByPolarity,
  strategyRdDefaultSideByPool,
  strategyRdDefaultTradeSideForPolarity,
  strategyRdInvertSideByLevel,
  strategyRdInvertSideByPolarity,
  strategyRdInvertSideByPool,
  normalizeStrategyRdPoolPolarity,
  strategyRdIsRejectionLevelSide,
  strategyRdSetupSideOptions,
  strategyRdSideByLevelFromSetup,
  strategyRdSideByLocationFromSlot,
  strategyRdSideByPolarityFromSetup,
  strategyRdSideByPoolFromSetup,
  strategyRdSideSelectLabel,
  strategyRdCollapseSideByPolarity,
} from "../../../utils/strategyRdComposer";
import {
  strategyRdApplyBuiltConfluenceSlot,
  strategyRdApplyBuiltSetupSlot,
  strategyRdBuildSlotFromSettings,
  strategyRdConceptIsLocationInteraction,
  strategyRdConceptSettings,
  strategyRdDefaultSettingUiValues,
  strategyRdLocationInteractionFamily,
  strategyRdMatchesVisibleWhen,
  strategyRdSettingEnumOptions,
  strategyRdSettingIsEnumMulti,
  strategyRdEntrySideFieldLabel,
  strategyRdIsSignalishEntryMode,
  strategyRdIsMaBookSetupId,
  strategyRdSetupPolarityKind,
  strategyRdSetupShowsTradeSide,
  strategyRdSideOptionsFromSettings,
  strategyRdUiValuesFromSlot,
  type StrategyRdSettingUiValues,
} from "../../../utils/strategyRdSettings";
import { formatExecutionCandleTypeLabel } from "../../../utils/executionCandleTimeframe";
import {
  strategyRdBrandActionButtonClassName,
  strategyRdBrandIconButtonClassName,
  strategyRdMetaClassName,
  strategyRdSectionTitleClassName,
} from "./strategyRdCardShell";
import { StrategyRdInfo, strategyRdCaptionClassName, strategyRdSectionCardClassName } from "./strategyRdLabUi";
import { StrategyRdLogicPathPanel } from "./StrategyRdLogicPathPanel";
import { StrategyRdSetupConditionsField } from "./StrategyRdSetupConditionsField";
import {
  strategyRdIsConditionSetup,
  strategyRdSetupConditions,
  strategyRdSetupMirror,
  strategyRdWithSetupConditions,
  strategyRdWithSetupMirror,
} from "../../../utils/strategyRdConditionSetup";

/** Dense composer selects size to their labels instead of truncating in narrow cells. */
function ComposerFieldSelect(
  props: React.ComponentProps<typeof FieldSelectFullWidth>
): JSX.Element {
  const { fitToLongestOption = true, ...rest } = props;
  return <FieldSelectFullWidth {...rest} fitToLongestOption={fitToLongestOption} />;
}

const MAX_WAIT_UNIT_OPTIONS: Array<{ value: StrategyRdMaxWait["unit"]; label: string }> = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "bars", label: "Bars" },
  { value: "none", label: "No limit" },
];

/**
 * Entry → Max wait. Empty = BE default (24 bars of the setup TF); "No limit" sends
 * `{ unit: "none" }`; otherwise `{ value, unit }` counted from the setup bar's close.
 */
function EntryMaxWaitField({
  entry,
  onEntryChange,
}: {
  entry: StrategyRdComposerEntrySlot;
  onEntryChange: (next: StrategyRdComposerEntrySlot) => void;
}): JSX.Element {
  const [unit, setUnit] = useState<StrategyRdMaxWait["unit"]>(entry.max_wait?.unit || "hours");
  const shownUnit = entry.max_wait?.unit || (unit === "none" ? "hours" : unit);
  const noLimit = shownUnit === "none";
  const shownValue =
    !noLimit && entry.max_wait?.value != null && entry.max_wait.value > 0
      ? String(entry.max_wait.value)
      : "";
  const [text, setText] = useState(shownValue);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(shownValue);
  }, [focused, shownValue]);

  const commit = (nextUnit: StrategyRdMaxWait["unit"], raw: string) => {
    if (nextUnit === "none") {
      onEntryChange({ ...entry, max_wait: { unit: "none" } });
      return;
    }
    const n = Number(raw);
    if (raw.trim() === "" || !Number.isFinite(n) || !(n > 0)) {
      const cleared = { ...entry };
      delete cleared.max_wait;
      onEntryChange(cleared);
      return;
    }
    onEntryChange({ ...entry, max_wait: { value: n, unit: nextUnit } });
  };

  return (
    <Field>
      <FieldLabel htmlFor="strategy-rd-entry-max-wait" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
        Max wait
      </FieldLabel>
      {/* Fits its column: the box flexes so the unit select never runs under the TF field. */}
      <div className="flex h-8 min-w-0 items-center gap-2">
        <Input
          id="strategy-rd-entry-max-wait"
          className="min-w-0 flex-1"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={noLimit ? "No limit" : "Default (24 bars)"}
          disabled={noLimit}
          value={noLimit ? "" : text}
          onFocus={() => setFocused(true)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
            setText(raw);
            if (raw.trim() === "" || raw === "." || raw.endsWith(".")) return;
            commit(shownUnit, raw);
          }}
          onBlur={() => {
            setFocused(false);
            if (noLimit) return;
            if (text.trim() === "" || !(Number(text) > 0)) {
              setText("");
              commit(shownUnit, "");
              return;
            }
            commit(shownUnit, text);
          }}
        />
        <div className="w-[6.5rem] shrink-0">
          <ComposerFieldSelect
            id="strategy-rd-entry-max-wait-unit"
            value={shownUnit}
            onValueChange={(next) => {
              const nextUnit: StrategyRdMaxWait["unit"] =
                next === "days" || next === "bars" || next === "none" ? next : "hours";
              setUnit(nextUnit);
              // No limit, or a unit change on a typed value; leaving No limit with an
              // empty box returns to the default.
              commit(nextUnit, nextUnit === "none" ? "" : text);
            }}
            options={MAX_WAIT_UNIT_OPTIONS}
          />
        </div>
      </div>
    </Field>
  );
}

/** "Optional" on a filter confluence (N of M). Optional slots must be on the setup TF. */
function OptionalConfluenceToggle({
  index,
  checked,
  offSetupTf,
  onChange,
}: {
  index: number;
  checked: boolean;
  offSetupTf: boolean;
  onChange: (optional: boolean) => void;
}): JSX.Element {
  const id = `strategy-rd-confluence-optional-${index}`;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Checkbox
        id={id}
        checked={checked}
        disabled={offSetupTf && !checked}
        onCheckedChange={(v) => onChange(v === true)}
      />
      <label htmlFor={id}>
        Optional: counts toward &quot;at least N&quot; instead of having to pass
        {offSetupTf ? " (only on the setup timeframe)" : ""}
      </label>
    </div>
  );
}

const ENTRY_OPTIONS: Array<{ value: StrategyRdEntryMode; label: string }> = [
  { value: "signal_close", label: "Signal close" },
  { value: "next_open", label: "Next open" },
  { value: "break_close", label: "Break close" },
  { value: "retest_then_close", label: "Retest then close" },
  { value: "rejection", label: "Rejection" },
  { value: "limit_at_level", label: "Limit at level" },
];

/** Shown only while Rules → Entry is a custom rule; picking a preset here turns it off. */
const ENTRY_RULE_OPTION: { value: StrategyRdEntryMode; label: string } = {
  value: "rule",
  label: "Custom rule (Rules → Entry)",
};

/** Catalog name with HTF / FVG / MA expanded for the picker. */
function conceptPickerLabel(concept: Pick<StrategyRdConcept, "id" | "name">): string {
  return strategyRdConceptDisplayName(concept.id, concept.name);
}

function sideSelectOptions(
  sides: StrategyRdSlotSide[],
  opts?: { polarityKind?: "pattern" | "level" | "pool" | "none" }
): Array<{ value: StrategyRdSlotSide; label: string }> {
  return sides.map((value) => ({
    value,
    label: strategyRdSideSelectLabel(value, opts),
  }));
}

function BranchHintChip({ text }: { text: string }): JSX.Element {
  return (
    <p
      data-strategy-rd-branch-hint=""
      className={cn(
        "inline-flex max-w-full rounded-[6px] border border-[color:var(--brand-tropical-surf)]/35",
        "bg-[color-mix(in_oklab,var(--brand-tropical-surf)_12%,transparent)]",
        "px-2 py-1 font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-white"
      )}
    >
      {text}
    </p>
  );
}

/**
 * One horizontal composer row: fields size to content; TF + action stay last.
 * Override Field's default `w-full` so selects sit side-by-side (not stacked).
 */
const COMPOSER_ROW_CLASS = cn(
  "flex flex-wrap items-end gap-x-2 gap-y-3 pb-0.5",
  "[&_[data-slot=field]]:!w-fit [&_[data-slot=field]]:max-w-none [&_[data-slot=field]]:shrink-0",
  "[&_[data-slot=field]]:*:!w-auto"
);

const SETTINGS_INLINE_CLASS = cn(
  "flex flex-nowrap items-end gap-2",
  "[&_[data-slot=field]]:!w-fit [&_[data-slot=field]]:shrink-0",
  "[&_[data-slot=field]]:*:!w-auto"
);

function LockedTfField({
  value,
  label = "TF",
}: {
  value: string;
  label?: string;
}): JSX.Element {
  return (
    <Field className="!w-[4.5rem] shrink-0">
      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{label}</FieldLabel>
      <div
        className="flex h-8 w-full items-center rounded-[6px] border border-border bg-muted/40 px-2 text-sm text-muted-foreground"
        title="Locked to Step 1 setup timeframe"
      >
        {formatExecutionCandleTypeLabel(value) || value}
      </div>
    </Field>
  );
}

/**
 * All controls on one row; TF is always the last control before the action slot.
 */
function ComposerAlignRow({
  fields,
  tf,
  action,
}: {
  fields: React.ReactNode[];
  tf: React.ReactNode;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <div className={COMPOSER_ROW_CLASS} data-strategy-rd-composer-row="">
      {fields.filter((node) => node != null && node !== false)}
      <div className="ml-auto flex shrink-0 items-end gap-2 pl-1">
        {tf}
        <div className="flex min-w-[2.25rem] items-end justify-end pb-0.5">
          {action ?? <span className="hidden size-8 sm:block" aria-hidden />}
        </div>
      </div>
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className={cn(strategyRdMetaClassName, "text-[13px] leading-snug")}>{children}</p>;
}

/**
 * The catalog's What / Use / Recommend text: behind an info icon, not paragraphs in the form.
 */
function ConceptDescriptionBlock({
  description,
}: {
  description: string;
}): JSX.Element | null {
  const sections = strategyRdConceptDescriptionSections(description);
  if (sections.length === 0) return null;
  return (
    <div data-strategy-rd-concept-description="" className="flex items-center gap-1.5">
      <span className="font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-sidebar-foreground">
        How it works
      </span>
      <StrategyRdInfo title="How it works">
        {sections.map((section, index) => (
          <p key={`${section.label || "body"}-${index}`}>
            {section.label ? <strong>{section.label}:</strong> : null} {section.text}
          </p>
        ))}
      </StrategyRdInfo>
    </div>
  );
}

const COACH_TOUCH_CLASS =
  "ring-2 ring-[color:var(--brand-tropical-surf)] shadow-[0_0_0_3px_color-mix(in_oklab,var(--brand-tropical-surf)_22%,transparent)]";

function SummaryColumns({
  columns,
  litTitles,
}: {
  columns: ReturnType<typeof strategyRdComposerSummaryColumns>;
  litTitles?: ReadonlySet<string>;
}): JSX.Element {
  return (
    // Stacked label / value rows; three columns only when the panel is wide enough for them.
    <div className="@container/recipe mt-3">
      <div className="grid grid-cols-1 gap-3 @[46rem]/recipe:grid-cols-3">
        {columns.map((col) => (
          <div
            key={col.title}
            className={cn(
              "flex min-w-0 flex-col gap-2 !rounded-md !border !border-solid !border-border bg-background p-4 dark:bg-sidebar",
              litTitles?.has(col.title) && COACH_TOUCH_CLASS
            )}
          >
            <p className={cn(strategyRdCaptionClassName, "m-0")}>{col.title}</p>
            <dl className="m-0 flex flex-col">
              {col.rows.map((row) => (
                <div
                  key={`${col.title}-${row.label}`}
                  className="flex items-baseline justify-between gap-4 !border-0 !border-t !border-solid !border-border py-1.5 first:!border-t-0"
                >
                  <dt className="shrink-0 font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-sidebar-foreground">
                    {row.label}
                  </dt>
                  <dd
                    className="m-0 min-w-0 text-right font-[family-name:var(--font-primary)] text-sm font-medium leading-snug text-[color:var(--brand-tropical-surf)]"
                    title={row.value}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

function settingFieldLabel(setting: StrategyRdConceptSetting): string {
  return (setting.label || "").trim() || setting.key;
}

function ConceptSettingsFields({
  concept,
  uiValues,
  onUiValuesChange,
  lockedCandleType,
  omitKeys,
  fieldIdPrefix,
  /** Flatten into parent grid (one cell per setting). */
  inline = false,
  polarityKind = "none",
}: {
  concept: StrategyRdConcept | null;
  uiValues: StrategyRdSettingUiValues;
  onUiValuesChange: (next: StrategyRdSettingUiValues) => void;
  /** When set, candle_type is shown locked (setup TF) instead of editable. */
  lockedCandleType?: string | null;
  omitKeys?: string[];
  fieldIdPrefix: string;
  inline?: boolean;
  polarityKind?: "pattern" | "level" | "pool" | "none";
}): JSX.Element | null {
  const settings = strategyRdConceptSettings(concept);
  if (settings.length === 0) return null;
  const hidden = new Set((omitKeys || []).map((k) => k.trim()).filter(Boolean));

  const visible = settings.filter(
    (s) =>
      !hidden.has(s.key) && strategyRdMatchesVisibleWhen(s.visible_when, uiValues)
  );
  if (visible.length === 0) return null;

  return (
    <div className={inline ? "contents" : SETTINGS_INLINE_CLASS}>
      {visible.map((setting) => {
        const key = setting.key;
        if (key === "candle_type" && lockedCandleType) {
          return (
            <Field key={key}>
              <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                {settingFieldLabel(setting)}
              </FieldLabel>
              <div
                className="flex h-8 items-center rounded-[6px] border border-border bg-muted/40 px-2.5 text-sm text-muted-foreground"
                title="Locked to Step 1 setup timeframe"
              >
                {formatExecutionCandleTypeLabel(lockedCandleType) || lockedCandleType}
              </div>
            </Field>
          );
        }

        if (strategyRdSettingIsEnumMulti(setting)) {
          const enumOpts = strategyRdSettingEnumOptions(setting);
          const options =
            enumOpts.length > 0
              ? enumOpts.map((row) => ({ value: row.value, label: row.label }))
              : [];
          const selected = (uiValues[key] || String(setting.default ?? ""))
            .split(",")
            .map((part) => part.trim())
            .filter(Boolean);
          return (
            <Field key={key}>
              <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                {settingFieldLabel(setting)}
              </FieldLabel>
              <FieldMultiSelectFullWidth
                id={`${fieldIdPrefix}-${key}`}
                selected={selected}
                onChange={(next) =>
                  onUiValuesChange({
                    ...uiValues,
                    [key]: next.map((v) => v.trim()).filter(Boolean).join(","),
                  })
                }
                options={options}
                placeholder={setting.label || "Select"}
              />
            </Field>
          );
        }

        if (setting.value_kind === "enum" || (setting.enum_values && setting.enum_values.length > 0)) {
          const enumOpts = strategyRdSettingEnumOptions(setting);
          const options =
            enumOpts.length > 0
              ? key === "side"
                ? enumOpts.map((row) => {
                    const side = normalizeStrategyRdSlotSide(row.value);
                    return {
                      value: row.value,
                      label: side
                        ? strategyRdSideSelectLabel(side, { polarityKind })
                        : row.label,
                    };
                  })
                : enumOpts.map((row) => ({
                    value: row.value,
                    label: row.label,
                    ...(row.description ? { description: row.description } : {}),
                  }))
              : key === "side"
                ? sideSelectOptions(["long", "short", "both", "invert"], { polarityKind })
                : key === "candle_type"
                  ? STRATEGY_RD_CANDLE_TYPES.map((tf) => ({
                      value: tf,
                      label: formatExecutionCandleTypeLabel(tf) || tf,
                    }))
                  : [];
          const value =
            uiValues[key] ||
            String(setting.default ?? "") ||
            options[0]?.value ||
            "";
          return (
            <Field key={key}>
              <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                {settingFieldLabel(setting)}
              </FieldLabel>
              <ComposerFieldSelect
                id={`${fieldIdPrefix}-${key}`}
                value={value}
                onValueChange={(next) => onUiValuesChange({ ...uiValues, [key]: next })}
                options={options}
                fitToLongestOption
              />
            </Field>
          );
        }

        return null;
      })}
    </div>
  );
}

function defaultSlotFromConcept(
  concept: StrategyRdConcept | null,
  opts: {
    setupCandleType: string;
    role: "setup" | "confluence";
    /** Sibling confluence slots — prefer prior Reject on same location family. */
    siblingConfluences?: readonly StrategyRdComposerConfluenceSlot[];
  }
): StrategyRdComposerSetupSlot | StrategyRdComposerConfluenceSlot {
  const id = concept?.id || "";
  const settings = strategyRdConceptSettings(concept);
  const ui = strategyRdDefaultSettingUiValues(settings);
  if (!ui.side) {
    ui.side =
      opts.role === "setup"
        ? strategyRdDefaultSetupSide(concept)
        : strategyRdDefaultConfluenceSide(concept);
  }
  // Pattern polarity (bull/bear) may default Trade side to match. Level (high/low) stays independent.
  if (opts.role === "setup" && strategyRdSetupPolarityKind(settings) === "pattern") {
    const matched = strategyRdDefaultTradeSideForPolarity(ui.polarity);
    if (matched) ui.side = matched;
  }
  if (!ui.candle_type && opts.role === "confluence") {
    ui.candle_type = opts.setupCandleType;
  }
  // Location chips: keep Respect by default; reuse Reject if same family already chose it.
  if (opts.role === "confluence" && strategyRdConceptIsLocationInteraction(concept)) {
    const family = strategyRdLocationInteractionFamily(id);
    const priorReject = (opts.siblingConfluences || []).some((slot) => {
      if (strategyRdLocationInteractionFamily(slot.id) !== family) return false;
      const ix = slot.params && typeof slot.params.interaction !== "undefined"
        ? String(slot.params.interaction).trim().toLowerCase()
        : "";
      return ix === "reject";
    });
    if (priorReject && (ui.interaction === "respect" || !ui.interaction)) {
      // Only for zone/event enums that include reject (not trendline break modes).
      const interactionSetting = settings.find((s) => s.key === "interaction" && s.param);
      const allowsReject = (interactionSetting?.enum_values || []).some(
        (row) => String(row.value).trim().toLowerCase() === "reject"
      );
      if (allowsReject) ui.interaction = "reject";
    }
  }
  const built = strategyRdBuildSlotFromSettings(
    concept ? { ...concept, id } : { id, settings: null },
    ui,
    { omitCandleType: opts.role === "setup" }
  );
  const catalogName = concept?.name?.trim() || null;
  const catalogDescription = concept?.description?.trim() || null;
  const withCatalogCopy = <T extends { id: string }>(slot: T): T => ({
    ...slot,
    ...(catalogName ? { name: catalogName } : {}),
    ...(catalogDescription ? { description: catalogDescription } : {}),
  });
  if (opts.role === "setup") {
    const applied = strategyRdApplyBuiltSetupSlot(built);
    const polarityKind = strategyRdSetupPolarityKind(settings);
    if (polarityKind === "none") {
      // Neutrals: lock Both. MA cross etc. keep catalog Trade side (Both / Invert / Long / Short).
      if (!strategyRdSetupShowsTradeSide(settings)) {
        return withCatalogCopy({
          ...applied,
          side: "both",
          side_by_polarity: undefined,
        });
      }
      return withCatalogCopy({
        ...applied,
        side_by_polarity: undefined,
      });
    }
    if (polarityKind === "pattern" && normalizeStrategyRdPolarity(ui.polarity) === "both") {
      const map = strategyRdDefaultSideByPolarity();
      return withCatalogCopy({
        ...applied,
        side: strategyRdCollapseSideByPolarity(map),
        side_by_polarity: map,
      });
    }
    if (polarityKind === "level" && normalizeStrategyRdLevelPolarity(ui.polarity) === "both") {
      const map = strategyRdDefaultSideByLevel();
      return withCatalogCopy({
        ...applied,
        side: strategyRdCollapseSideByPolarity(map),
        side_by_polarity: map,
      });
    }
    if (polarityKind === "pool" && normalizeStrategyRdPoolPolarity(ui.polarity) === "both") {
      const map = strategyRdDefaultSideByPool();
      return withCatalogCopy({
        ...applied,
        side: strategyRdCollapseSideByPolarity(map),
        side_by_polarity: map,
      });
    }
    return withCatalogCopy(applied);
  }
  const confluence = strategyRdApplyBuiltConfluenceSlot(built);
  if (!confluence.candle_type && opts.setupCandleType) {
    confluence.candle_type = opts.setupCandleType;
  }
  const hasLocationParam = settings.some((s) => s.key === "polarity" && s.param);
  const location = (ui.polarity || "").trim().toLowerCase();
  if (hasLocationParam && location === "both") {
    const map = strategyRdDefaultSideByLocation();
    return withCatalogCopy({
      ...confluence,
      side: strategyRdCollapseSideByPolarity(map),
      side_by_polarity: map,
    });
  }
  return withCatalogCopy(confluence);
}

function StepHeader({
  step,
  title,
  guide,
  action,
}: {
  step: number;
  title: string;
  guide: string;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    // Settings-card header (Profile settings): brand title, muted description, no step circle.
    <div className="flex items-start justify-between gap-3" data-step={step}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h4 className="m-0 font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">{title}</h4>
        <p className="m-0 font-[family-name:var(--font-primary)] text-sm leading-snug text-gray-500 dark:text-white">{guide}</p>
      </div>
      {action ? <div className="shrink-0 pt-0.5">{action}</div> : null}
    </div>
  );
}

function StepCard({
  step,
  title,
  guide,
  action,
  highlighted = false,
  stepKey,
  children,
}: {
  step: number;
  title: string;
  guide: string;
  action?: React.ReactNode;
  highlighted?: boolean;
  stepKey?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      data-strategy-rd-composer-step={stepKey || title.toLowerCase()}
      className={cn(
        strategyRdSectionCardClassName,
        highlighted && COACH_TOUCH_CLASS
      )}
    >
      <StepHeader step={step} title={title} guide={guide} action={action} />
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

export type StrategyRdComposerProps = {
  catalog: StrategyRdCatalog;
  setupCandleType: string;
  setup: StrategyRdComposerSetupSlot;
  onSetupChange: (next: StrategyRdComposerSetupSlot) => void;
  confluences: StrategyRdComposerConfluenceSlot[];
  onConfluencesChange: (next: StrategyRdComposerConfluenceSlot[]) => void;
  entry: StrategyRdComposerEntrySlot;
  onEntryChange: (next: StrategyRdComposerEntrySlot) => void;
  srScopes: string[];
  onSrScopesChange: (next: string[]) => void;
  sessionIds: string[];
  onSessionIdsChange: (next: string[]) => void;
  activePackId: string | null;
  onPackSelect: (pack: StrategyRdPack) => void;
  confluenceSkipped?: string[] | null;
  /**
   * Runtime volume from OHLC window (`volume_available` / `volume_status`).
   * `null` = unknown (pre-preview) — do not gate on asset class.
   */
  volumeAvailable?: boolean | null;
  /** BE logic_path — show as “what this means” after preview/scan. */
  logicPath?: StrategyRdLogicPath | null;
  collapsed?: boolean;
  onEditCollapsed?: () => void;
  /** Bumps when TES writes the composer, so touched controls can flash. */
  coachFlashToken?: number;
  coachFlashKeys?: string[];
  /** N of M: at least this many `optional` confluences must pass. */
  optionalMin?: number;
  onOptionalMinChange?: (next: number) => void;
};

export function StrategyRdComposer({
  catalog,
  setupCandleType,
  setup,
  onSetupChange,
  confluences,
  onConfluencesChange,
  entry,
  onEntryChange,
  srScopes,
  onSrScopesChange,
  sessionIds,
  onSessionIdsChange,
  activePackId,
  onPackSelect,
  confluenceSkipped = null,
  volumeAvailable = null,
  logicPath = null,
  collapsed = false,
  onEditCollapsed,
  coachFlashToken = 0,
  coachFlashKeys = [],
  optionalMin = 1,
  onOptionalMinChange,
}: StrategyRdComposerProps): JSX.Element {
  const optionalCount = confluences.filter((c) => c.optional === true).length;
  const [coachFlashLit, setCoachFlashLit] = React.useState(false);
  useEffect(() => {
    if (!coachFlashToken) return;
    setCoachFlashLit(true);
    const stepKey = coachFlashKeys.includes("entry")
      ? "entry"
      : coachFlashKeys.some((key) => key.startsWith("confluence"))
        ? "confluences"
        : coachFlashKeys.includes("setup") || coachFlashKeys.includes("sessions")
          ? "setup"
          : null;
    if (stepKey) {
      window.requestAnimationFrame(() => {
        document
          .querySelector(`[data-strategy-rd-composer-step="${stepKey}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    const timer = window.setTimeout(() => setCoachFlashLit(false), 2600);
    return () => window.clearTimeout(timer);
  }, [coachFlashToken, coachFlashKeys]);
  const flashOn = coachFlashLit && coachFlashKeys.length > 0;
  const flashHas = (key: string) => flashOn && coachFlashKeys.includes(key);
  const flashSummaryTitles = new Set<string>();
  if (flashHas("setup") || flashHas("sessions") || flashHas("timeframe")) {
    flashSummaryTitles.add("Setup");
  }
  if (coachFlashKeys.some((key) => key.startsWith("confluence:")) || flashHas("timeframe")) {
    if (flashOn) flashSummaryTitles.add("Confluences");
  }
  if (flashHas("entry") || flashHas("timeframe")) flashSummaryTitles.add("Entry");
  const setups = useMemo(() => strategyRdSetupsFromCatalog(catalog), [catalog]);
  const confluenceConcepts = useMemo(
    () => strategyRdConfluencesFromCatalog(catalog),
    [catalog]
  );
  const packs = catalog.packs;
  const sessionRequired = strategyRdSetupNeedsSession(setup.id, catalog);
  const allSentinel = strategyRdAllSessionSentinel(catalog);
  const selectedSetup = useMemo(
    () => strategyRdSetupConceptFromCatalog(catalog, setup.id),
    [catalog, setup.id]
  );
  const setupSettingsForKind = useMemo(
    () => strategyRdConceptSettings(selectedSetup),
    [selectedSetup]
  );
  const showsSetupTradeSide = strategyRdSetupShowsTradeSide(setupSettingsForKind);

  // Neutrals: lock Setup Trade side to Both (recipe book has nothing to book).
  useEffect(() => {
    if (showsSetupTradeSide) return;
    const side = normalizeStrategyRdSlotSide(setup.side);
    if ((side === "both" || !side) && !setup.side_by_polarity) return;
    onSetupChange({
      ...setup,
      side: "both",
      side_by_polarity: undefined,
    });
  }, [
    showsSetupTradeSide,
    setup,
    onSetupChange,
  ]);

  // Prefer runtime flag; fall back to soft-skip reasons when flag absent.
  const volumeBlocked =
    volumeAvailable === false ||
    (volumeAvailable == null && strategyRdVolumeUnavailable(confluenceSkipped));
  const preferVolumeChips = volumeAvailable === true;
  const selectedVolumeConfluence = confluences.some((slot) => {
    const concept =
      confluenceConcepts.find((c) => c.id === slot.id) ||
      catalog.concepts.find((c) => c.id === slot.id) ||
      null;
    return concept ? strategyRdIsVolumeConfluence(concept) : false;
  });

  const labelForConcept = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of catalog.concepts) {
      byId.set(c.id, conceptPickerLabel(c));
    }
    return (id: string) => byId.get(id) || strategyRdConceptDisplayName(id);
  }, [catalog.concepts]);

  const setupOptions = useMemo(
    () =>
      setups.map((c) => ({
        value: c.id,
        label: conceptPickerLabel(c),
      })),
    [setups]
  );

  const setupOptionGroups = useMemo(
    () =>
      strategyRdSetupPickerGroups(catalog).map((group) => ({
        id: group.id,
        label: group.label,
        options: group.concepts.map((c) => ({
          value: c.id,
          label: conceptPickerLabel(c),
        })),
      })),
    [catalog]
  );

  const sessionOptions = useMemo(
    () => strategyRdSessionSelectOptions(catalog),
    [catalog]
  );
  const sessionOptionGroups = useMemo(
    () => strategyRdSessionPickerGroups(catalog),
    [catalog]
  );

  const confluenceOptions = useMemo(() => {
    let rows = confluenceConcepts.filter(
      (c) => !(volumeBlocked && strategyRdIsVolumeConfluence(c))
    );
    if (preferVolumeChips) {
      rows = [...rows].sort((a, b) => {
        const av = strategyRdIsVolumeConfluence(a) ? 0 : 1;
        const bv = strategyRdIsVolumeConfluence(b) ? 0 : 1;
        return av - bv;
      });
    }
    return rows.map((c) => ({
      value: c.id,
      label: conceptPickerLabel(c),
    }));
  }, [confluenceConcepts, preferVolumeChips, volumeBlocked]);

  const confluenceOptionGroups = useMemo(() => {
    const rows = confluenceConcepts.filter(
      (c) => !(volumeBlocked && strategyRdIsVolumeConfluence(c))
    );
    const groups = strategyRdConfluencePickerGroups(rows);
    const ordered = preferVolumeChips
      ? [
          ...groups.filter((g) => g.id === "volume"),
          ...groups.filter((g) => g.id !== "volume"),
        ]
      : groups;
    return ordered.map((group) => ({
      id: group.id,
      label: group.label,
      options: group.concepts.map((c) => ({
        value: c.id,
        label: conceptPickerLabel(c),
      })),
    }));
  }, [confluenceConcepts, preferVolumeChips, volumeBlocked]);

  const candleOptions = useMemo(
    () =>
      STRATEGY_RD_CANDLE_TYPES.map((tf) => ({
        value: tf,
        label: formatExecutionCandleTypeLabel(tf) || tf,
      })),
    []
  );

  const packOptions = useMemo(
    () =>
      packs.map((pack) => ({
        value: pack.id,
        label: pack.name || pack.id,
      })),
    [packs]
  );

  const summaryColumns = useMemo(
    () =>
      strategyRdComposerSummaryColumns({
        setup,
        confluences,
        entry,
        setupCandleType,
        sessionIds,
        setupPolarityKind: strategyRdSetupPolarityKind(
          strategyRdConceptSettings(selectedSetup)
        ),
        labelForConcept,
        labelForEntry: strategyRdEntryModeLabel,
        labelForSession: (id) => strategyRdSessionDisplayName(catalog, id),
        settingsForConcept: (id) =>
          strategyRdConceptSettings(confluenceConcepts.find((c) => c.id === id) || null),
      }),
    [
      catalog,
      confluenceConcepts,
      confluences,
      entry,
      labelForConcept,
      selectedSetup,
      sessionIds,
      setup,
      setupCandleType,
    ]
  );

  if (collapsed) {
    return (
      <div
        data-strategy-rd-composer-collapsed=""
        className={cn(
          "flex flex-col gap-3 rounded-[6px] px-3 py-2.5",
          "border border-[color:var(--brand-tropical-surf)]/40",
          "bg-[color-mix(in_oklab,var(--brand-tropical-surf)_10%,transparent)]",
          "dark:bg-[color-mix(in_oklab,var(--brand-tropical-surf)_16%,transparent)]"
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className={strategyRdSectionTitleClassName}>Recipe</p>
            <p className={cn(STRATEGY_PINE_SECTION_LABEL_CLASS, "mt-1")}>Your selections</p>
          </div>
          {onEditCollapsed ? (
            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              className={strategyRdBrandActionButtonClassName}
              onClick={onEditCollapsed}
            >
              <Pencil className="size-3.5" aria-hidden />
              Edit
            </Button>
          ) : null}
        </div>
        <SummaryColumns columns={summaryColumns} litTitles={flashSummaryTitles} />
      </div>
    );
  }

  const setupHintDef = strategyRdConceptShortDefinition(setup.id, selectedSetup?.name);
  const setupTfGuidance = strategyRdConceptTfGuidance(setup.id);
  const setupDescription = strategyRdConceptDescriptionForDisplay(
    setup.id,
    setup.description?.trim() || selectedSetup?.description?.trim() || ""
  );
  const setupPolarityKind = strategyRdSetupPolarityKind(
    strategyRdConceptSettings(selectedSetup)
  );
  const setupReading = setup.id
    ? strategyRdConceptSideReading({
        conceptId: setup.id,
        conceptName: selectedSetup?.name || setup.name,
        side: setup.side,
        polarity: setup.polarity,
        levelPolarity: setupPolarityKind === "level",
        poolPolarity: setupPolarityKind === "pool",
      })
    : "";

  return (
    <div data-strategy-rd-composer="" className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div
          data-strategy-rd-recipe-summary=""
          className="rounded-[6px] border border-border bg-muted/30 px-4 py-3"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className={strategyRdCaptionClassName}>Your recipe</p>
            <p className="font-[family-name:var(--font-secondary)] text-[11px] text-muted-foreground">
              Setup → confluences → entry · setup timeframe{" "}
              {formatExecutionCandleTypeLabel(setupCandleType) || setupCandleType}
            </p>
          </div>
          <div>
            <SummaryColumns columns={summaryColumns} litTitles={flashSummaryTitles} />
          </div>
        </div>
      </div>

      {packs.length > 0 ? (
        <StepCard
          step={1}
          title="Start from a pack"
          guide="Optional shortcut: apply a ready recipe, then tweak below."
        >
          <Field>
            <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Pack</FieldLabel>
            <ComposerFieldSelect
              id="strategy-rd-pack"
              value={activePackId || ""}
              onValueChange={(packId) => {
                const pack = packs.find((p) => p.id === packId);
                if (pack) onPackSelect(pack);
              }}
              options={[{ value: "", label: "None: build manually" }, ...packOptions]}
              placeholder="Apply a pack recipe"
            />
          </Field>
        </StepCard>
      ) : null}

      <StepCard
        step={packs.length > 0 ? 2 : 1}
        title="Setup"
        stepKey="setup"
        guide="Pattern, Level, or Pool plus Trade side come from the catalog. Preview loads Logic path (how we read settings, branches, path markers)."
        highlighted={flashHas("setup") || flashHas("sessions") || flashHas("timeframe")}
      >
        {(() => {
          const setupSettings = strategyRdConceptSettings(selectedSetup);
          const setupUi = strategyRdUiValuesFromSlot(setupSettings, setup, {
            side: strategyRdDefaultSetupSide(selectedSetup),
          });
          const polarityKind = strategyRdSetupPolarityKind(setupSettings);
          // MA cross: golden/death is implied — Trade side labels use Pattern (Bull/Bear) wording.
          const tradeSideLabelKind =
            polarityKind !== "none"
              ? polarityKind
              : strategyRdIsMaBookSetupId(setup.id)
                ? "pattern"
                : "none";
          const patternPolarity =
            normalizeStrategyRdPolarity(setupUi.polarity) ||
            normalizeStrategyRdPolarity(
              setup.polarity != null ? String(setup.polarity) : null
            );
          const levelPolarity =
            normalizeStrategyRdLevelPolarity(setupUi.polarity) ||
            normalizeStrategyRdLevelPolarity(
              setup.polarity != null ? String(setup.polarity) : null
            );
          const poolPolarity =
            normalizeStrategyRdPoolPolarity(setupUi.polarity) ||
            normalizeStrategyRdPoolPolarity(
              setup.polarity != null ? String(setup.polarity) : null
            );
          const patternIsBoth = polarityKind === "pattern" && patternPolarity === "both";
          const levelIsBoth = polarityKind === "level" && levelPolarity === "both";
          const poolIsBoth = polarityKind === "pool" && poolPolarity === "both";
          const branchIsBoth = patternIsBoth || levelIsBoth || poolIsBoth;
          const sideByPolarity = strategyRdSideByPolarityFromSetup(setup);
          const sideByLevel = strategyRdSideByLevelFromSetup(setup);
          const sideByPool = strategyRdSideByPoolFromSetup(setup);
          const tradeSideOptions = strategyRdSideOptionsFromSettings(setupSettings);
          const hasInvertOption = tradeSideOptions.includes("invert");
          // Catalog Trade side carries Both / Invert — skip per-branch pickers when invert exists.
          const showBranchPickers = branchIsBoth && !hasInvertOption;
          const branchTradeOptions = sideSelectOptions(["long", "short"]);

          const mapForTradeSide = (
            side: StrategyRdSlotSide
          ): {
            pattern?: ReturnType<typeof strategyRdDefaultSideByPolarity>;
            level?: ReturnType<typeof strategyRdDefaultSideByLevel>;
            pool?: ReturnType<typeof strategyRdDefaultSideByPool>;
          } => {
            if (polarityKind === "level") {
              if (side === "invert") return { level: strategyRdInvertSideByLevel() };
              if (side === "long") return { level: { high: "long", low: "long" } };
              if (side === "short") return { level: { high: "short", low: "short" } };
              return { level: strategyRdDefaultSideByLevel() };
            }
            if (polarityKind === "pool") {
              if (side === "invert") return { pool: strategyRdInvertSideByPool() };
              if (side === "long") return { pool: { buyside: "long", sellside: "long" } };
              if (side === "short") return { pool: { buyside: "short", sellside: "short" } };
              return { pool: strategyRdDefaultSideByPool() };
            }
            if (polarityKind === "pattern") {
              if (side === "invert") return { pattern: strategyRdInvertSideByPolarity() };
              if (side === "long") return { pattern: { bull: "long", bear: "long" } };
              if (side === "short") return { pattern: { bull: "short", bear: "short" } };
              return { pattern: strategyRdDefaultSideByPolarity() };
            }
            return {};
          };

          const applySetupUi = (nextUi: StrategyRdSettingUiValues) => {
            const ui: StrategyRdSettingUiValues = { ...nextUi };
            const prevPol = setupUi.polarity || "";
            const nextPol = ui.polarity || "";
            const nextPolNorm = normalizeStrategyRdPolarity(nextPol);
            const nextLevelNorm = normalizeStrategyRdLevelPolarity(nextPol);
            const nextPoolNorm = normalizeStrategyRdPoolPolarity(nextPol);
            let nextSideBy = setup.side_by_polarity
              ? strategyRdSideByPolarityFromSetup(setup)
              : null;
            let nextSideByLevel: ReturnType<typeof strategyRdDefaultSideByLevel> | null =
              null;
            let nextSideByPool: ReturnType<typeof strategyRdDefaultSideByPool> | null = null;

            if (polarityKind === "pattern" && nextPol !== prevPol) {
              if (nextPolNorm === "both") {
                nextSideBy = strategyRdDefaultSideByPolarity();
                ui.side = strategyRdCollapseSideByPolarity(nextSideBy);
              } else {
                nextSideBy = null;
                const matched = strategyRdDefaultTradeSideForPolarity(nextPol);
                if (matched) ui.side = matched;
              }
            }

            if (polarityKind === "level" && nextPol !== prevPol) {
              if (nextLevelNorm === "both") {
                nextSideByLevel = strategyRdDefaultSideByLevel();
                ui.side = strategyRdCollapseSideByPolarity(nextSideByLevel);
                nextSideBy = null;
              } else if (nextLevelNorm === "high" || nextLevelNorm === "low") {
                nextSideByLevel = null;
                nextSideBy = null;
              }
            }

            if (polarityKind === "pool" && nextPol !== prevPol) {
              if (nextPoolNorm === "both") {
                nextSideByPool = strategyRdDefaultSideByPool();
                ui.side = strategyRdCollapseSideByPolarity(nextSideByPool);
                nextSideBy = null;
              } else if (nextPoolNorm === "buyside" || nextPoolNorm === "sellside") {
                nextSideByPool = null;
                nextSideBy = null;
              }
            }

            // Trade side Both / Invert / Long / Short while polarity = both → remap branches.
            if (branchIsBoth && ui.side !== setupUi.side) {
              const sideNorm = normalizeStrategyRdSlotSide(ui.side) || "both";
              const mapped = mapForTradeSide(sideNorm);
              if (mapped.level) {
                nextSideByLevel = mapped.level;
                ui.side = strategyRdCollapseSideByPolarity(mapped.level);
              }
              if (mapped.pool) {
                nextSideByPool = mapped.pool;
                ui.side = strategyRdCollapseSideByPolarity(mapped.pool);
              }
              if (mapped.pattern) {
                nextSideBy = mapped.pattern;
                ui.side = strategyRdCollapseSideByPolarity(mapped.pattern);
              }
            }

            const built = strategyRdBuildSlotFromSettings(selectedSetup, ui, {
              omitCandleType: true,
            });
            const applied = strategyRdApplyBuiltSetupSlot(built, setup);
            if (polarityKind === "level" && nextLevelNorm === "both" && nextSideByLevel) {
              onSetupChange({
                ...applied,
                side: strategyRdCollapseSideByPolarity(nextSideByLevel),
                side_by_polarity: nextSideByLevel,
                invert: strategyRdCollapseSideByPolarity(nextSideByLevel) === "invert",
              });
            } else if (polarityKind === "pool" && nextPoolNorm === "both" && nextSideByPool) {
              onSetupChange({
                ...applied,
                side: strategyRdCollapseSideByPolarity(nextSideByPool),
                side_by_polarity: nextSideByPool,
                invert: strategyRdCollapseSideByPolarity(nextSideByPool) === "invert",
              });
            } else if (polarityKind === "pattern" && nextPolNorm === "both" && nextSideBy) {
              onSetupChange({
                ...applied,
                side: strategyRdCollapseSideByPolarity(nextSideBy),
                side_by_polarity: nextSideBy,
                invert: strategyRdCollapseSideByPolarity(nextSideBy) === "invert",
              });
            } else {
              const { side_by_polarity: _drop, invert: _inv, ...rest } = applied;
              onSetupChange(rest);
            }
          };

          const onBranchTradeChange = (
            branch: "bull" | "bear",
            trade: StrategyRdSlotSide
          ) => {
            const nextMap = {
              ...sideByPolarity,
              [branch]: trade === "short" ? "short" : "long",
            } as { bull: "long" | "short"; bear: "long" | "short" };
            const collapsed = strategyRdCollapseSideByPolarity(nextMap);
            onSetupChange({
              ...setup,
              polarity: "both",
              side: collapsed,
              side_by_polarity: nextMap,
              invert: collapsed === "invert",
            });
          };

          const onLevelBranchTradeChange = (
            branch: "high" | "low",
            trade: StrategyRdSlotSide
          ) => {
            const nextMap = {
              ...sideByLevel,
              [branch]: trade === "short" ? "short" : "long",
            } as { high: "long" | "short"; low: "long" | "short" };
            const collapsed = strategyRdCollapseSideByPolarity(nextMap);
            onSetupChange({
              ...setup,
              polarity: "both",
              side: collapsed,
              side_by_polarity: nextMap,
              invert: collapsed === "invert",
            });
          };

          const onPoolBranchTradeChange = (
            branch: "buyside" | "sellside",
            trade: StrategyRdSlotSide
          ) => {
            const nextMap = {
              ...sideByPool,
              [branch]: trade === "short" ? "short" : "long",
            } as { buyside: "long" | "short"; sellside: "long" | "short" };
            const collapsed = strategyRdCollapseSideByPolarity(nextMap);
            onSetupChange({
              ...setup,
              polarity: "both",
              side: collapsed,
              side_by_polarity: nextMap,
              invert: collapsed === "invert",
            });
          };

          const settingsOmit = [
            "candle_type",
            ...(!showsSetupTradeSide ? ["side"] : []),
          ];

          const polarityAndSideFields =
            setupSettings.length > 0 ? (
              <ConceptSettingsFields
                concept={selectedSetup}
                uiValues={{
                  ...setupUi,
                  side:
                    normalizeStrategyRdSlotSide(setup.side) ||
                    setupUi.side ||
                    strategyRdDefaultSetupSide(selectedSetup),
                }}
                onUiValuesChange={applySetupUi}
                omitKeys={settingsOmit}
                fieldIdPrefix="strategy-rd-setup"
                inline
                polarityKind={tradeSideLabelKind}
              />
            ) : showsSetupTradeSide ? (
              <Field>
                <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Trade side</FieldLabel>
                <ComposerFieldSelect
                  id="strategy-rd-setup-side"
                  value={
                    normalizeStrategyRdSlotSide(setup.side) ||
                    strategyRdDefaultSetupSide(selectedSetup)
                  }
                  onValueChange={(side) => {
                    const sideNorm = (side as StrategyRdSlotSide) || "both";
                    if (branchIsBoth) {
                      const mapped = mapForTradeSide(sideNorm);
                      const map =
                        mapped.level || mapped.pool || mapped.pattern || null;
                      if (map) {
                        onSetupChange({
                          ...setup,
                          side: strategyRdCollapseSideByPolarity(map),
                          side_by_polarity: map,
                          invert: strategyRdCollapseSideByPolarity(map) === "invert",
                        });
                        return;
                      }
                    }
                    onSetupChange({
                      ...setup,
                      side: sideNorm,
                      side_by_polarity: undefined,
                      invert: sideNorm === "invert",
                    });
                  }}
                  options={sideSelectOptions(tradeSideOptions, {
                    polarityKind: tradeSideLabelKind,
                  })}
                />
              </Field>
            ) : null;

          const conceptField = (
            <Field key="concept" className="!w-fit min-w-[11rem] max-w-[16rem] shrink-0">
              <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Concept</FieldLabel>
              <ComposerFieldSelect
                id="strategy-rd-setup"
                value={setup.id}
                onValueChange={(id) => {
                  const nextConcept =
                    strategyRdSetupConceptFromCatalog(catalog, id) ||
                    setups.find((c) => c.id === id) ||
                    null;
                  onSetupChange(
                    defaultSlotFromConcept(nextConcept, {
                      setupCandleType,
                      role: "setup",
                    }) as StrategyRdComposerSetupSlot
                  );
                }}
                options={setupOptions}
                optionGroups={setupOptionGroups}
                placeholder="Choose a setup"
                fitToLongestOption={false}
                title={
                  strategyRdConceptDescriptionForDisplay(
                    setup.id,
                    setup.description?.trim() || selectedSetup?.description?.trim() || ""
                  ) || undefined
                }
              />
            </Field>
          );

          const sessionsField = (
            <Field
              key="sessions"
              className={cn(
                "!w-fit min-w-[8.5rem] shrink-0",
                flashHas("sessions") && COACH_TOUCH_CLASS
              )}
            >
              <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                Sessions{sessionRequired ? "" : " (optional)"}
              </FieldLabel>
              <FieldMultiSelectFullWidth
                id="strategy-rd-sessions"
                selected={sessionIds}
                onChange={(next) =>
                  onSessionIdsChange(
                    normalizeStrategyRdSessionIdsSelection(next, {
                      allSentinel,
                      previous: sessionIds,
                    })
                  )
                }
                options={sessionOptions}
                optionGroups={sessionOptionGroups}
                placeholder="Select sessions"
              />
            </Field>
          );

          const setupLeadingFields: React.ReactNode[] = [
            conceptField,
            ...(strategyRdIsConditionSetup(setup.id)
              ? [
                  <StrategyRdSetupConditionsField
                    key="conditions"
                    value={strategyRdSetupConditions(setup)}
                    onChange={(next) => onSetupChange(strategyRdWithSetupConditions(setup, next))}
                  />,
                ]
              : []),
            polarityAndSideFields,
            ...(showBranchPickers && patternIsBoth
              ? [
                  <Field key="bull-trade">
                    <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                      Bullish → trade
                    </FieldLabel>
                    <ComposerFieldSelect
                      id="strategy-rd-setup-bull-trade"
                      value={sideByPolarity.bull}
                      onValueChange={(side) =>
                        onBranchTradeChange("bull", side as StrategyRdSlotSide)
                      }
                      options={branchTradeOptions}
                    />
                  </Field>,
                  <Field key="bear-trade">
                    <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                      Bearish → trade
                    </FieldLabel>
                    <ComposerFieldSelect
                      id="strategy-rd-setup-bear-trade"
                      value={sideByPolarity.bear}
                      onValueChange={(side) =>
                        onBranchTradeChange("bear", side as StrategyRdSlotSide)
                      }
                      options={branchTradeOptions}
                    />
                  </Field>,
                ]
              : []),
            ...(showBranchPickers && levelIsBoth
              ? [
                  <Field key="high-trade">
                    <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                      High → trade
                    </FieldLabel>
                    <ComposerFieldSelect
                      id="strategy-rd-setup-high-trade"
                      value={sideByLevel.high}
                      onValueChange={(side) =>
                        onLevelBranchTradeChange("high", side as StrategyRdSlotSide)
                      }
                      options={branchTradeOptions}
                    />
                  </Field>,
                  <Field key="low-trade">
                    <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                      Low → trade
                    </FieldLabel>
                    <ComposerFieldSelect
                      id="strategy-rd-setup-low-trade"
                      value={sideByLevel.low}
                      onValueChange={(side) =>
                        onLevelBranchTradeChange("low", side as StrategyRdSlotSide)
                      }
                      options={branchTradeOptions}
                    />
                  </Field>,
                ]
              : []),
            ...(showBranchPickers && poolIsBoth
              ? [
                  <Field key="buyside-trade">
                    <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                      Buyside → trade
                    </FieldLabel>
                    <ComposerFieldSelect
                      id="strategy-rd-setup-buyside-trade"
                      value={sideByPool.buyside}
                      onValueChange={(side) =>
                        onPoolBranchTradeChange("buyside", side as StrategyRdSlotSide)
                      }
                      options={branchTradeOptions}
                    />
                  </Field>,
                  <Field key="sellside-trade">
                    <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                      Sellside → trade
                    </FieldLabel>
                    <ComposerFieldSelect
                      id="strategy-rd-setup-sellside-trade"
                      value={sideByPool.sellside}
                      onValueChange={(side) =>
                        onPoolBranchTradeChange("sellside", side as StrategyRdSlotSide)
                      }
                      options={branchTradeOptions}
                    />
                  </Field>,
                ]
              : []),
            sessionsField,
          ];

          return (
            <>
              <div
                data-strategy-rd-setup-row={branchIsBoth ? "both" : "single"}
                className={cn(
                  (flashHas("setup") || flashHas("timeframe")) &&
                    cn("rounded-[6px] p-1", COACH_TOUCH_CLASS)
                )}
              >
                <ComposerAlignRow
                  fields={setupLeadingFields}
                  tf={<LockedTfField value={setupCandleType} />}
                />
                {strategyRdIsConditionSetup(setup.id) ? (
                  <label
                    className="mt-2 flex w-fit cursor-pointer items-start gap-2 font-[family-name:var(--font-secondary)] text-xs text-gray-500 dark:text-white"
                    data-strategy-rd-setup-mirror=""
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 size-3.5 accent-[color:var(--brand-tropical-surf)]"
                      checked={strategyRdSetupMirror(setup)}
                      onChange={(e) => onSetupChange(strategyRdWithSetupMirror(setup, e.target.checked))}
                    />
                    <span className="flex flex-col gap-0.5">
                      <span className="font-medium">Trade both ways</span>
                      <span className="text-muted-foreground">
                        Shorts trade these conditions as picked; longs trade their opposites (overbought ↔ oversold,
                        upper ↔ lower band, uptrend ↔ downtrend).
                      </span>
                    </span>
                  </label>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                {setupDescription ? (
                  <ConceptDescriptionBlock description={setupDescription} />
                ) : patternIsBoth ? (
                  <Hint>
                    <span className="font-medium text-[color:var(--brand-tropical-surf)]">
                      {strategyRdCollapseSideByPolarity(sideByPolarity) === "invert"
                        ? "Polarity map: Bull → Short · Bear → Long"
                        : `Bullish → ${sideByPolarity.bull}, bearish → ${sideByPolarity.bear}`}
                    </span>
                    {". "}
                    {hasInvertOption
                      ? "Trade side Bull Long · Bear Short = matched; Bull Short · Bear Long = invert. Entry can stay Both."
                      : "Pick the trade for each pattern when scanning both. After Preview, Logic path shows the full fork."}
                  </Hint>
                ) : levelIsBoth ? (
                  <Hint>
                    <span className="font-medium text-[color:var(--brand-tropical-surf)]">
                      {strategyRdCollapseSideByPolarity(sideByLevel) === "invert"
                        ? "Polarity map: High → Short · Low → Long"
                        : `High → ${sideByLevel.high}, low → ${sideByLevel.low}`}
                    </span>
                    {". "}
                    {hasInvertOption
                      ? "Trade side High Long · Low Short = breakout map; High Short · Low Long = fade. Entry can stay Both."
                      : "Pick the trade for each level when scanning both. After Preview, Logic path shows the full fork."}
                  </Hint>
                ) : poolIsBoth ? (
                  <Hint>
                    <span className="font-medium text-[color:var(--brand-tropical-surf)]">
                      {strategyRdCollapseSideByPolarity(sideByPool) === "invert"
                        ? "Polarity map: Buyside → Short · Sellside → Long"
                        : `Buyside → ${sideByPool.buyside}, sellside → ${sideByPool.sellside}`}
                    </span>
                    {". "}
                    {hasInvertOption
                      ? "Trade side Buyside Long · Sellside Short = matched; Invert = fade map. Entry can stay Both."
                      : "Pick the trade for each pool side when scanning both. After Preview, Logic path shows the full fork."}
                  </Hint>
                ) : (
                  <Hint>
                    {setupReading ? (
                      <>
                        <span className="font-medium text-[color:var(--brand-tropical-surf)]">{setupReading}</span>
                        {". "}
                      </>
                    ) : null}
                    {polarityKind === "pattern"
                      ? "Set Pattern (bull/bear/both) and Trade side. After Preview, Logic path shows the full fork."
                      : setup.id === "liq_sweep" || setup.id === "liq_run"
                        ? "Pool picks which swing was taken (high/buyside or low/sellside). When Pool is Both, set the trade for high and low separately."
                        : polarityKind === "level"
                          ? "Level filters which extreme is active. When Level is Both, set the trade for high and low separately."
                          : polarityKind === "pool"
                            ? "Pool filters which sweep side is active. When Pool is Both, set the trade for buyside and sellside separately."
                            : setupHintDef ||
                              "After Preview, Logic path shows what this recipe means for Long and Short."}
                  </Hint>
                )}
                {setupTfGuidance ? <Hint>{setupTfGuidance}</Hint> : null}
              </div>
            </>
          );
        })()}
      </StepCard>

      <StepCard
        step={packs.length > 0 ? 3 : 2}
        title="Confluences"
        stepKey="confluences"
        highlighted={
          flashHas("timeframe") ||
          (flashOn && coachFlashKeys.some((key) => key.startsWith("confluence:")))
        }
        guide="Location · Zone chips arm → interact → fill. Location · Event chips only confirm (never own Enter). Context chips stay boolean filters."
        action={
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            className={strategyRdBrandActionButtonClassName}
            onClick={() => {
              const first = confluenceOptions.find(
                (opt) => !confluences.some((c) => c.id === opt.value)
              );
              if (!first) return;
              const concept = confluenceConcepts.find((c) => c.id === first.value) || null;
              onConfluencesChange([
                ...confluences,
                defaultSlotFromConcept(concept, {
                  setupCandleType,
                  role: "confluence",
                  siblingConfluences: confluences,
                }) as StrategyRdComposerConfluenceSlot,
              ]);
            }}
            disabled={confluenceOptions.every((opt) =>
              confluences.some((c) => c.id === opt.value)
            )}
          >
            <Plus className="size-3.5" aria-hidden />
            Add
          </Button>
        }
      >
        {volumeBlocked ? (
          <Hint>
            Volume unavailable on this series. Volume chips are hidden and any selected
            volume filters soft-pass (skipped).
          </Hint>
        ) : preferVolumeChips ? (
          <Hint>
            Volume is available on this series. Prefer RVOL / expansion / dry-up chips when
            they fit the recipe.
          </Hint>
        ) : null}
        {volumeBlocked && selectedVolumeConfluence ? (
          <Hint>
            Selected volume filters will be skipped until volume is available.
          </Hint>
        ) : null}
        {confluences.length === 0 ? (
          <Hint>No filters yet. Preview still works with setup alone.</Hint>
        ) : (
          confluences.map((slot, index) => {
            const concept =
              confluenceConcepts.find((c) => c.id === slot.id) ||
              catalog.concepts.find((c) => c.id === slot.id) ||
              null;
            const settings = strategyRdConceptSettings(concept);
            const uiValues = strategyRdUiValuesFromSlot(settings, slot, {
              side: strategyRdDefaultConfluenceSide(concept),
              candle_type: setupCandleType,
            });
            const hasLocationParam = settings.some(
              (s) => s.key === "polarity" && s.param
            );
            const locationPolarity = (uiValues.polarity || "").trim().toLowerCase();
            const locationIsBoth = hasLocationParam && locationPolarity === "both";
            const sideByLocation = strategyRdSideByLocationFromSlot(slot);
            const branchTradeOptions = sideSelectOptions(["long", "short"]);

            const applyUi = (nextUi: StrategyRdSettingUiValues) => {
              const ui: StrategyRdSettingUiValues = { ...nextUi };
              const prevLoc = (uiValues.polarity || "").trim().toLowerCase();
              const nextLoc = (ui.polarity || "").trim().toLowerCase();
              let nextSideBy = slot.side_by_polarity
                ? strategyRdSideByLocationFromSlot(slot)
                : null;

              if (hasLocationParam && nextLoc !== prevLoc) {
                if (nextLoc === "both") {
                  nextSideBy = strategyRdDefaultSideByLocation();
                  ui.side = strategyRdCollapseSideByPolarity(nextSideBy);
                } else {
                  nextSideBy = null;
                }
              }

              const built = strategyRdBuildSlotFromSettings(concept, ui);
              const applied = strategyRdApplyBuiltConfluenceSlot(built, slot);
              const tfFromUi = (ui.candle_type || "").trim();
              const withTf = (row: StrategyRdComposerConfluenceSlot): StrategyRdComposerConfluenceSlot => {
                if (tfFromUi && tfFromUi !== "inherit") {
                  return { ...row, candle_type: tfFromUi };
                }
                return row;
              };
              const next = [...confluences];
              if (nextLoc === "both" && nextSideBy) {
                next[index] = withTf({
                  ...applied,
                  side: strategyRdCollapseSideByPolarity(nextSideBy),
                  side_by_polarity: nextSideBy,
                });
              } else {
                const { side_by_polarity: _drop, ...rest } = applied;
                next[index] = withTf(rest);
              }
              onConfluencesChange(next);
            };

            const onLocationBranchTradeChange = (
              branch: "above" | "below",
              trade: StrategyRdSlotSide
            ) => {
              const nextMap = {
                ...sideByLocation,
                [branch]: trade === "short" ? "short" : "long",
              } as { above: "long" | "short"; below: "long" | "short" };
              const next = [...confluences];
              next[index] = {
                ...slot,
                params: {
                  ...(slot.params || {}),
                  polarity: "both",
                },
                side: strategyRdCollapseSideByPolarity(nextMap),
                side_by_polarity: nextMap,
              };
              onConfluencesChange(next);
            };

            const sideValue =
              normalizeStrategyRdSlotSide(slot.side) ||
              strategyRdDefaultConfluenceSide(concept);
            const confluenceDescription =
              concept?.description?.trim() || slot.description?.trim() || "";
            const hintDef = strategyRdConceptDescriptionForDisplay(
              slot.id,
              confluenceDescription,
              "confluence"
            );
            const hintFallback =
              strategyRdConceptShortDefinition(slot.id, concept?.name || slot.name) || "";
            const reading = locationIsBoth
              ? ""
              : strategyRdConceptSideReading({
                  conceptId: slot.id,
                  conceptName: concept?.name,
                  side: sideValue,
                  polarity: slot.polarity != null ? String(slot.polarity) : null,
                  locationPolarity:
                    slot.params && typeof slot.params.polarity !== "undefined"
                      ? String(slot.params.polarity)
                      : null,
                });
            const branchChip = strategyRdBranchHintLabel(concept, labelForConcept);
            const isLocationIx = strategyRdConceptIsLocationInteraction(concept);
            const interactionValue = (uiValues.interaction || "respect").trim();
            const settingsOmit = [
              "candle_type",
              ...(locationIsBoth ? ["side"] : []),
              // Location chips: Interaction / Timing / Structure rendered in fixed order below.
              ...(isLocationIx ? ["interaction", "timing", "structure"] : []),
            ];

            const interactionSetting = settings.find((s) => s.key === "interaction");
            const interactionOption = strategyRdSettingEnumOptions(interactionSetting).find(
              (row) => row.value === interactionValue
            );
            const interactionHelp =
              interactionOption?.description ||
              strategyRdLocationInteractionMeaning(slot.id, interactionValue);
            const timingSetting = settings.find((s) => s.key === "timing");
            const structureSetting = settings.find((s) => s.key === "structure");

            const enumField = (
              setting: StrategyRdConceptSetting | undefined,
              key: string,
              fieldKey: string
            ): React.ReactNode => {
              if (!setting) return null;
              const enumOpts = strategyRdSettingEnumOptions(setting);
              const options =
                enumOpts.length > 0
                  ? enumOpts.map((row) => ({
                      value: row.value,
                      label: row.label,
                      ...(row.description ? { description: row.description } : {}),
                    }))
                  : [];
              if (options.length === 0) return null;
              const value = uiValues[key] || String(setting.default ?? "") || options[0]?.value || "";
              return (
                <Field key={fieldKey}>
                  <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                    {(setting.label || "").trim() || key}
                  </FieldLabel>
                  <ComposerFieldSelect
                    id={`strategy-rd-confluence-${index}-${key}`}
                    value={value}
                    onValueChange={(next) => applyUi({ ...uiValues, [key]: next })}
                    options={options}
                  />
                </Field>
              );
            };

            const leadingFields: React.ReactNode[] = [
              <Field
                key="concept"
                className={cn(
                  "!w-fit min-w-[11rem] max-w-[16rem] shrink-0",
                  (flashHas(`confluence:${index}`) || flashHas("timeframe")) && COACH_TOUCH_CLASS
                )}
              >
                <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Concept</FieldLabel>
                <ComposerFieldSelect
                  id={`strategy-rd-confluence-${index}`}
                  value={slot.id}
                  placeholder="Choose a concept"
                  onValueChange={(id) => {
                    const nextConcept =
                      confluenceConcepts.find((c) => c.id === id) || null;
                    const next = [...confluences];
                    next[index] = defaultSlotFromConcept(nextConcept, {
                      setupCandleType,
                      role: "confluence",
                      siblingConfluences: confluences.filter((_, i) => i !== index),
                    }) as StrategyRdComposerConfluenceSlot;
                    onConfluencesChange(next);
                  }}
                  options={confluenceOptions.map((opt) => {
                    const usedElsewhere = confluences.some(
                      (c, i) => i !== index && c.id === opt.value
                    );
                    return {
                      ...opt,
                      label: usedElsewhere ? `${opt.label} · added` : opt.label,
                    };
                  })}
                  optionGroups={confluenceOptionGroups.map((group) => ({
                    ...group,
                    options: group.options.map((opt) => {
                      const usedElsewhere = confluences.some(
                        (c, i) => i !== index && c.id === opt.value
                      );
                      return {
                        ...opt,
                        label: usedElsewhere ? `${opt.label} · added` : opt.label,
                      };
                    }),
                  }))}
                  fitToLongestOption={false}
                  title={
                    strategyRdConceptDescriptionForDisplay(
                      slot.id,
                      confluenceDescription,
                      "confluence"
                    ) || undefined
                  }
                />
              </Field>,
              ...(isLocationIx ? [enumField(structureSetting, "structure", "structure")] : []),
              ...(isLocationIx ? [enumField(interactionSetting, "interaction", "interaction")] : []),
              settings.length > 0 ? (
                <ConceptSettingsFields
                  key="settings"
                  concept={concept}
                  uiValues={uiValues}
                  onUiValuesChange={applyUi}
                  omitKeys={settingsOmit}
                  fieldIdPrefix={`strategy-rd-confluence-${index}`}
                  inline
                />
              ) : !locationIsBoth ? (
                <Field key="side">
                  <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                    Trade side
                  </FieldLabel>
                  <ComposerFieldSelect
                    id={`strategy-rd-confluence-side-${index}`}
                    value={sideValue}
                    onValueChange={(side) => {
                      const next = [...confluences];
                      next[index] = { ...slot, side: side as StrategyRdSlotSide };
                      onConfluencesChange(next);
                    }}
                    options={sideSelectOptions(strategyRdConfluenceSideOptions(concept))}
                  />
                </Field>
              ) : null,
              ...(locationIsBoth
                ? [
                    <Field key="above-trade">
                      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                        Above → trade
                      </FieldLabel>
                      <ComposerFieldSelect
                        id={`strategy-rd-confluence-above-trade-${index}`}
                        value={sideByLocation.above}
                        onValueChange={(side) =>
                          onLocationBranchTradeChange(
                            "above",
                            side as StrategyRdSlotSide
                          )
                        }
                        options={branchTradeOptions}
                      />
                    </Field>,
                    <Field key="below-trade">
                      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                        Below → trade
                      </FieldLabel>
                      <ComposerFieldSelect
                        id={`strategy-rd-confluence-below-trade-${index}`}
                        value={sideByLocation.below}
                        onValueChange={(side) =>
                          onLocationBranchTradeChange(
                            "below",
                            side as StrategyRdSlotSide
                          )
                        }
                        options={branchTradeOptions}
                      />
                    </Field>,
                  ]
                : []),
              ...(isLocationIx ? [enumField(timingSetting, "timing", "timing")] : []),
              // Recipe-level S/R scopes live on the Clear-of-S/R chip's row (first one only).
              strategyRdHasClearOfOpposingSr([slot.id]) &&
              confluences.findIndex((c) => strategyRdHasClearOfOpposingSr([c.id])) === index ? (
                <Field key="sr-scopes" className="!w-fit min-w-[8.5rem] shrink-0">
                  <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>S/R scopes</FieldLabel>
                  <FieldMultiSelectFullWidth
                    id="strategy-rd-sr-scopes"
                    selected={srScopes}
                    onChange={onSrScopesChange}
                    options={STRATEGY_RD_SR_SCOPE_OPTIONS}
                    placeholder="Day / week / month"
                  />
                </Field>
              ) : null,
            ];

            return (
              <div
                key={`${slot.id}-${index}`}
                // Each confluence its own block inside the Confluences card.
                className="flex flex-col gap-3 !rounded-md !border !border-solid !border-border bg-background p-4 dark:bg-sidebar"
                data-strategy-rd-confluence-row={
                  isLocationIx ? "location" : locationIsBoth ? "both" : "single"
                }
              >
                <ComposerAlignRow
                  fields={leadingFields}
                  tf={
                    <Field>
                      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>TF</FieldLabel>
                      <ComposerFieldSelect
                        id={`strategy-rd-confluence-tf-${index}`}
                        value={(slot.candle_type || setupCandleType) as string}
                        onValueChange={(tf) => {
                          const next = [...confluences];
                          const nextUi = { ...uiValues, candle_type: tf };
                          if (settings.length > 0) {
                            applyUi(nextUi);
                          } else {
                            next[index] = { ...slot, candle_type: tf };
                            onConfluencesChange(next);
                          }
                        }}
                        options={candleOptions}
                      />
                    </Field>
                  }
                  action={
                    <Button
                      type="button"
                      variant="outline-brand"
                      size="icon"
                      className={strategyRdBrandIconButtonClassName}
                      aria-label="Remove confluence"
                      onClick={() =>
                        onConfluencesChange(confluences.filter((_, i) => i !== index))
                      }
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </Button>
                  }
                />
                {hintDef ? (
                  <ConceptDescriptionBlock description={hintDef} />
                ) : isLocationIx ? (
                  <Hint>
                    <span className="font-medium text-[color:var(--brand-tropical-surf)]">
                      {interactionHelp || "Location interaction"}
                    </span>
                    {". "}
                    {strategyRdLocationInteractionHelper(slot.id)}
                    {strategyRdLocationBreakerSubtitle(slot.id)
                      ? ` ${strategyRdLocationBreakerSubtitle(slot.id)}`
                      : null}
                    {strategyRdLocationEntryModeHint(interactionValue, slot.id)
                      ? ` ${strategyRdLocationEntryModeHint(interactionValue, slot.id)}`
                      : null}
                  </Hint>
                ) : branchChip && sideValue === "both" && !locationIsBoth ? (
                  <BranchHintChip text={branchChip} />
                ) : locationIsBoth ? (
                  <Hint>
                    <span className="font-medium text-[color:var(--brand-tropical-surf)]">
                      Above → {sideByLocation.above}, below → {sideByLocation.below}
                    </span>
                    {". "}
                    Pick the trade for each location when scanning both.
                  </Hint>
                ) : reading ? (
                  <Hint>
                    <span className="font-medium text-[color:var(--brand-tropical-surf)]">{reading}</span>
                  </Hint>
                ) : hintFallback ? (
                  <Hint>{hintFallback}</Hint>
                ) : null}
                {/* N of M: filters only — zones / levels / events carry the fill and prices. */}
                {!isLocationIx && onOptionalMinChange ? (
                  <OptionalConfluenceToggle
                    index={index}
                    checked={slot.optional === true}
                    offSetupTf={
                      Boolean((slot.candle_type || "").trim()) &&
                      (slot.candle_type || "").trim() !== setupCandleType.trim()
                    }
                    onChange={(optional) => {
                      const next = [...confluences];
                      const { optional: _drop, ...rest } = slot;
                      next[index] = optional ? { ...rest, optional: true } : rest;
                      onConfluencesChange(next);
                    }}
                  />
                ) : null}
              </div>
            );
          })
        )}
        {optionalCount > 0 && onOptionalMinChange ? (
          <div
            className="flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-3 text-sm"
            data-strategy-rd-optional-min=""
          >
            <span className="text-muted-foreground">At least</span>
            <Input
              type="number"
              aria-label="At least N optional confluences"
              className="h-8 w-16 px-2"
              min={1}
              max={optionalCount}
              step={1}
              value={Math.min(Math.max(1, optionalMin), optionalCount)}
              onChange={(e) => {
                const n = Math.floor(Number(e.target.value));
                if (Number.isFinite(n)) onOptionalMinChange(Math.min(Math.max(1, n), optionalCount));
              }}
            />
            <span className="text-muted-foreground">
              of these {optionalCount} optional confluence{optionalCount === 1 ? "" : "s"} must pass. The
              others stay required.
            </span>
          </div>
        ) : null}

      </StepCard>

      <StepCard
        step={packs.length > 0 ? 4 : 3}
        title="Entry"
        stepKey="entry"
        highlighted={flashHas("entry") || flashHas("timeframe")}
        guide="When and how the trade triggers after the pattern prints."
      >
        <>
            <ComposerAlignRow
              fields={[
                <Field key="mode">
                  <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Mode</FieldLabel>
                  <ComposerFieldSelect
                    id="strategy-rd-entry-mode"
                    value={parseStrategyRdEntryMode(entry.mode)}
                    onValueChange={(mode) => onEntryChange({ ...entry, mode })}
                    options={
                      entry.mode === "rule" ? [...ENTRY_OPTIONS, ENTRY_RULE_OPTION] : ENTRY_OPTIONS
                    }
                  />
                </Field>,
                <Field key="side">
                  <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                    {strategyRdEntrySideFieldLabel(showsSetupTradeSide)}
                  </FieldLabel>
                  <ComposerFieldSelect
                    id="strategy-rd-entry-side"
                    value={normalizeStrategyRdSlotSide(entry.side) || "both"}
                    onValueChange={(side) =>
                      onEntryChange({ ...entry, side: side as StrategyRdSlotSide })
                    }
                    options={sideSelectOptions(["long", "short", "both"])}
                  />
                </Field>,
                <EntryMaxWaitField key="max-wait" entry={entry} onEntryChange={onEntryChange} />,
              ]}
              tf={
                <Field>
                  <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>TF</FieldLabel>
                  <ComposerFieldSelect
                    id="strategy-rd-entry-tf"
                    value={(entry.candle_type || setupCandleType) as string}
                    onValueChange={(tf) => onEntryChange({ ...entry, candle_type: tf })}
                    options={candleOptions}
                  />
                </Field>
              }
            />
            <Hint>
              Max wait: counted from the setup bar&apos;s close. The confluence forming, price
              reaching it and the fill must all happen within it. Empty = 24 bars of the setup
              timeframe; pick No limit to wait indefinitely.
            </Hint>
            {(() => {
              const setupSideNorm = normalizeStrategyRdSlotSide(setup.side);
              const entrySideNorm = normalizeStrategyRdSlotSide(entry.side) || "both";
              const usingSetupChip =
                showsSetupTradeSide &&
                (setupSideNorm === "long" || setupSideNorm === "short") &&
                entrySideNorm === "both"
                  ? `Using Setup: ${setupSideNorm === "long" ? "Long" : "Short"}`
                  : null;
              const signalish = strategyRdIsSignalishEntryMode(entry.mode);
              let entryHint: React.ReactNode;
              if (!showsSetupTradeSide && signalish) {
                entryHint =
                  "This pattern is directionless. Set Entry → Trade side to Long or Short.";
              } else if (!showsSetupTradeSide) {
                entryHint = (
                  <>
                    {strategyRdEntryModeDefinition(entry.mode as StrategyRdEntryMode)}
                    {" Break / retest / rejection need a level (level setup or location confluence)."}
                  </>
                );
              } else if (strategyRdIsRejectionLevelSide(setup.polarity, setup.side)) {
                entryHint =
                  "Level = price · Setup Trade side = book · Entry Trade side = which directions may enter.";
              } else {
                entryHint = (
                  <>
                    {strategyRdEntryModeDefinition(entry.mode as StrategyRdEntryMode)}
                    {" Entry Trade side Both follows Setup Trade side."}
                  </>
                );
              }
              return (
                <div className="flex flex-col gap-1.5">
                  {usingSetupChip ? <BranchHintChip text={usingSetupChip} /> : null}
                  <Hint>{entryHint}</Hint>
                </div>
              );
            })()}
        </>
      </StepCard>

      {logicPath ? <StrategyRdLogicPathPanel logicPath={logicPath} /> : null}
    </div>
  );
}
